/**
 * Pagina articoli a drill-down per categoria merceologica.
 *
 * Vista a 3 livelli:
 *   1. Grid di categorie (root classificazioni) con conteggio articoli totale
 *   2. Click categoria → grid sottocategorie
 *   3. Click sottocategoria (o categoria senza figli) → tabella articoli paginata
 *
 * Il conteggio articoli e la lista articoli sono filtrati lato backend per
 * UO corrente (via posizione_default_id, vedi classificazioni/tree e magazzino/articoli).
 *
 * Regola 6.8: importa solo da @floraltech/ui.
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';

interface TreeNode {
  id: string;
  codice: string;
  descrizione: string;
  parent_id: string | null;
  count_articoli: number;
  count_articoli_totale: number;
  children: TreeNode[];
}

interface Articolo {
  id: string;
  sku_interno: string;
  descrizione: string | null;
  metodo_uscita: string;
  giacenza: number;
  soglia_riordino: number | null;
  soglia_minima: number | null;
  stato: string;
}

const PAGE_SIZE = 50;

const giacenzaVariant = (
  giacenza: number,
  soglia_minima: number | null,
  soglia_riordino: number | null,
): 'destructive' | 'secondary' | 'default' => {
  if (soglia_minima != null && giacenza < soglia_minima) return 'destructive';
  if (soglia_riordino != null && giacenza < soglia_riordino) return 'secondary';
  return 'default';
};

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const ArticoliBrowser: React.FC = () => {
  const navigate = useNavigate();
  const { qty } = useFormatters();

  const [tree, setTree] = useState<TreeNode[]>([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [errorTree, setErrorTree] = useState<string | null>(null);

  const [rootId, setRootId] = useState<string | null>(null);
  const [leafId, setLeafId] = useState<string | null>(null);

  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [articoli, setArticoli] = useState<Articolo[]>([]);
  const [articoliTotal, setArticoliTotal] = useState(0);
  const [loadingArticoli, setLoadingArticoli] = useState(false);
  const [errorArticoli, setErrorArticoli] = useState<string | null>(null);

  const fetchTree = useCallback(async () => {
    setLoadingTree(true);
    setErrorTree(null);
    try {
      const resp = await fetch(
        '/api/v1/classificazioni/tree?sistema=merceologica',
        { headers: authHeaders() },
      );
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      setTree((await resp.json()) as TreeNode[]);
    } catch (err) {
      setErrorTree(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingTree(false);
    }
  }, []);

  useEffect(() => {
    void fetchTree();
  }, [fetchTree]);

  const rootNode = useMemo(
    () => tree.find((n) => n.id === rootId) ?? null,
    [tree, rootId],
  );

  const leafNode = useMemo(() => {
    if (!rootNode || !leafId) return null;
    return rootNode.children.find((c) => c.id === leafId) ?? null;
  }, [rootNode, leafId]);

  const activeCategoriaId = leafId ?? rootId;

  const fetchArticoli = useCallback(async () => {
    if (!activeCategoriaId) {
      setArticoli([]);
      setArticoliTotal(0);
      return;
    }
    setLoadingArticoli(true);
    setErrorArticoli(null);
    try {
      const params = new URLSearchParams();
      params.set('categoria_merce_id', activeCategoriaId);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (q.trim()) params.set('q', q.trim());
      const resp = await fetch(`/api/v1/magazzino/articoli?${params}`, {
        headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Errore ${resp.status}`);
      const total = parseInt(resp.headers.get('x-total-count') ?? '0', 10);
      setArticoliTotal(Number.isFinite(total) ? total : 0);
      setArticoli((await resp.json()) as Articolo[]);
    } catch (err) {
      setErrorArticoli(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoadingArticoli(false);
    }
  }, [activeCategoriaId, page, q]);

  useEffect(() => {
    void fetchArticoli();
  }, [fetchArticoli]);

  const resetToRoot = () => {
    setRootId(null);
    setLeafId(null);
    setQ('');
    setPage(0);
  };

  const enterRoot = (id: string) => {
    setRootId(id);
    setLeafId(null);
    setQ('');
    setPage(0);
  };

  const enterLeaf = (id: string) => {
    setLeafId(id);
    setQ('');
    setPage(0);
  };

  const backToRoot = () => {
    setLeafId(null);
    setQ('');
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(articoliTotal / PAGE_SIZE));
  const showArticoli = !!activeCategoriaId && (
    leafNode !== null ||
    (rootNode !== null && rootNode.children.length === 0)
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Articoli</h1>
          <nav className="text-sm text-muted-foreground mt-1" aria-label="Breadcrumb">
            <button
              type="button"
              className="hover:underline"
              onClick={resetToRoot}
              disabled={!rootId}
            >
              Categorie
            </button>
            {rootNode && (
              <>
                <span className="mx-2">/</span>
                <button
                  type="button"
                  className="hover:underline"
                  onClick={backToRoot}
                  disabled={!leafId}
                >
                  {rootNode.descrizione}
                </button>
              </>
            )}
            {leafNode && (
              <>
                <span className="mx-2">/</span>
                <span>{leafNode.descrizione}</span>
              </>
            )}
          </nav>
        </div>
        <Button variant="outline" onClick={() => navigate('/magazzino/articoli/nuovo')}>
          Nuovo articolo
        </Button>
      </div>

      {loadingTree && <p className="text-sm text-muted-foreground">Caricamento categorie...</p>}
      {errorTree && <p className="text-sm text-destructive">Errore: {errorTree}</p>}

      {/* Livello 1: categorie root */}
      {!rootId && !loadingTree && !errorTree && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {tree
            .filter((n) => n.count_articoli_totale > 0)
            .map((n) => (
              <Card
                key={n.id}
                className="cursor-pointer transition hover:border-primary"
                onClick={() => enterRoot(n.id)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{n.descrizione}</CardTitle>
                </CardHeader>
                <CardContent className="flex items-center justify-between">
                  <Badge variant="default">{n.count_articoli_totale} articoli</Badge>
                  <span className="text-xs text-muted-foreground">
                    {n.children.length} sottocat.
                  </span>
                </CardContent>
              </Card>
            ))}
          {tree.filter((n) => n.count_articoli_totale > 0).length === 0 && (
            <p className="col-span-full text-sm text-muted-foreground">
              Nessun articolo nel ramo corrente.
            </p>
          )}
        </div>
      )}

      {/* Livello 2: sottocategorie */}
      {rootNode && !leafId && rootNode.children.length > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {rootNode.children.map((c) => (
            <Card
              key={c.id}
              className="cursor-pointer transition hover:border-primary"
              onClick={() => enterLeaf(c.id)}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{c.descrizione}</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="default">{c.count_articoli_totale} articoli</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Livello 3: articoli */}
      {showArticoli && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Cerca per SKU o descrizione"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              className="max-w-sm"
            />
            <span className="text-sm text-muted-foreground">
              {articoliTotal} articoli
            </span>
          </div>

          {loadingArticoli && <p className="text-sm text-muted-foreground">Caricamento...</p>}
          {errorArticoli && <p className="text-sm text-destructive">Errore: {errorArticoli}</p>}

          {!loadingArticoli && !errorArticoli && articoli.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Metodo</TableHead>
                      <TableHead className="text-right">Giacenza</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {articoli.map((a) => (
                      <TableRow
                        key={a.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/magazzino/articoli/${a.id}`)}
                      >
                        <TableCell className="font-mono text-xs">{a.sku_interno}</TableCell>
                        <TableCell>{a.descrizione ?? '—'}</TableCell>
                        <TableCell className="text-xs uppercase">{a.metodo_uscita}</TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant={giacenzaVariant(a.giacenza, a.soglia_minima, a.soglia_riordino)}
                          >
                            {qty(a.giacenza)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {!loadingArticoli && !errorArticoli && articoli.length === 0 && (
            <p className="text-sm text-muted-foreground">Nessun articolo in questa categoria.</p>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loadingArticoli}
              >
                Precedente
              </Button>
              <span className="text-sm text-muted-foreground">
                Pagina {page + 1} di {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page + 1 >= totalPages || loadingArticoli}
              >
                Successiva
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ArticoliBrowser;
